// profitCalcClient.js — browser-safe, read-only, no-I/O port of backend/profitCalc.js's
// calcRow() + profitmeasurer/src/App.js's suggestDiscount(), for the Styles table's
// "Suggest Disc" / "What-If Disc" toggles (2026-09-15, "full parity" checkpoint).
//
// Why this exists instead of a new backend route: calcRow is PURE MATH with no I/O of
// its own (see backend/profitCalc.js's own top comment) — its only inputs are a raw row
// shape ({_cat,_cp,_rsp,_disc,...}) and the profit_settings doc, both of which the Styles
// table already has in hand once a page loads (POST /api/profit/calc's results carry
// enough to reconstruct _ch, and GET /api/profit-settings gives the settings object
// directly). What-If recomputes on every keystroke of a %; Suggest-Discount does an
// up-to-200-iteration binary-style scan PER ROW PER RENDER. Round-tripping either to the
// server would mean a network call per keystroke (What-If) or per page-load (Suggest) for
// up to 100 rows at once — real latency for no benefit, since nothing here touches the
// database or depends on server-only state. Duplicating this small pure function client-
// side (same "can't require() a CommonJS backend module from browser code" pattern
// ProfitSettingsPanel.js already uses for its constants — see that file's top comment)
// keeps the interaction instant and matches the original standalone app's own behavior,
// which ran this exact function directly in the browser.
//
// UNCHANGED math — copied field-for-field from backend/profitCalc.js's calcRow(). If that
// file's formula ever changes, re-port it here the same way. Do not "simplify" anything.

export const NON_RMG_KEYS = ["saree", "unstitch", "unstitch ghagra choli"];
export const CH_COMPLEX = ["flipkart", "myntra"];
export const ALL_CH = ["myntra", "amazon", "nykaa", "ajio", "tatacliq", "soch", "flipkart"];
export const SIMPLE_ONLY = ["amazon", "nykaa", "ajio", "tatacliq", "soch"];

export function normCat(raw) {
  let s = (raw || "").toLowerCase().trim().replace(/\s+/g, " ");
  if (s.endsWith("sses") && s.length > 4) return s.slice(0, -2);
  if (s.endsWith("ss")) return s;
  if (s.endsWith("s") && s.length > 2) return s.slice(0, -1);
  return s;
}
export const isNonRMG = (k) => NON_RMG_KEYS.some((n) => k === n || k.includes(n));
export const getGST = (k, sp) => (isNonRMG(k) ? 5 : sp >= 2500 ? 18 : 5);

// 2026-10-26 (owner): Flipkart can mirror Myntra's whole rate card via the boolean `flipkart_mirror_myntra`
// stored in the profit settings. DEFAULT ON: absent/undefined counts as true (no DB write needed). Flipkart-specific
// keys mirrored: flipkart (commission tiers/categories/levels), flipkartGT (GT slabs), flipkartFixedFee, flipkartRL
// (return slabs, zone split, RL charges). Returns the same object when off; otherwise a shallow copy. Idempotent.
// Kept in step with frontend/src/utils/profitCalcClient.js (parity test in qa/suites/style-search.test.js).
const FLIPKART_MIRROR_KEYS = [["flipkart", "myntra"], ["flipkartGT", "myntraGT"], ["flipkartFixedFee", "myntraFixedFee"], ["flipkartRL", "myntraRL"]];
export function flipkartMirrorsMyntra(settings) { return !!settings && settings.flipkart_mirror_myntra !== false; }
export function effectiveProfitSettings(settings) {
  if (!flipkartMirrorsMyntra(settings)) return settings;
  const out = { ...settings };
  for (const [fk, mk] of FLIPKART_MIRROR_KEYS) out[fk] = settings[mk];
  return out;
}

function lookupSlab(slabs, value) {
  for (const s of slabs || []) {
    const mn = +(s.min || 0);
    const mx = s.max === "" || s.max == null ? Infinity : +s.max;
    if (value >= mn && value <= mx) return +(s.fee || s.commission || 0);
  }
  return 0;
}
function getReturnSlabs(settings, channel, catKey) {
  if (SIMPLE_ONLY.includes(channel)) { const rs = settings[channel]?.returnSlabs || {}; return rs[catKey] || rs["_default"] || []; }
  if (CH_COMPLEX.includes(channel)) { const rs = settings[`${channel}RL`]?.returnSlabs || {}; return rs[catKey] || rs["_default"] || []; }
  return [];
}
function getZoneSplit(settings, channel, catKey) {
  const splits = settings[`${channel}RL`]?.split || {};
  return splits[catKey] || splits["_default"] || {};
}
function effRet(sty, prod) { return Math.max(+(sty || 0), +(prod || 0)); }

// raw shape: { _cat, _cp, _rsp, _disc, _styleReturnPct, _chSalesReturn?, _salesQty? }
// — same contract as backend/profitCalc.js's calcRow; see that file for the full doc.
export function calcRow(raw, settings, discOverride) {
  settings = effectiveProfitSettings(settings);
  const { _cat: catKey, _cp: cp, _rsp: rsp, _disc: disc, _styleReturnPct: styleReturnPct, _chSalesReturn } = raw;
  const effectiveDisc = discOverride != null ? discOverride : disc;
  const sp = rsp * (1 - effectiveDisc / 100);
  const gst = getGST(catKey, sp);
  const spNoTax = sp / (1 + gst / 100);
  const ch = {};

  for (const c of SIMPLE_ONLY) {
    const s = settings[c] || {};
    const ct = s.commissionType || "pct", ft = s.freightType || "flat", basis = s.commissionBasis || "GOT";
    const commBase = basis === "NOT" ? spNoTax : sp;
    const commAmt = ct === "pct" ? commBase * (+(s.commissionPct || 0)) / 100 : +(s.commissionFlat || 0);
    const freightAmt = ft === "pct" ? sp * (+(s.freightPct || 0)) / 100 : +(s.freightFlat || 0);
    const prodRetPct = lookupSlab(getReturnSlabs(settings, c, catKey), sp);
    const chSR = _chSalesReturn?.[c];
    const actualRetPct = chSR && chSR.salesQty > 0 ? (chSR.returnQty / chSR.salesQty) * 100 : styleReturnPct;
    const effRetPct = effRet(actualRetPct, prodRetPct);
    const salesQtyS = _chSalesReturn ? (chSR?.salesQty || 0) : (raw._salesQty || 0);
    const returnQtyS = Math.round(salesQtyS * effRetPct / 100);
    const chargeFreightOnReturn = s.freightOnReturn !== "no";
    const returnCost = chargeFreightOnReturn && salesQtyS > 0 ? (returnQtyS * freightAmt) / salesQtyS : 0;
    const expense = commAmt + freightAmt + returnCost;
    ch[c] = { expense, profit: spNoTax - cp - expense, commAmt, freightAmt, returnCost, chargeFreightOnReturn, returnQty: returnQtyS, salesQty: salesQtyS, productReturnPct: prodRetPct, styleReturnPct: actualRetPct, effReturnPct: effRetPct, basis };
  }

  for (const c of CH_COMPLEX) {
    const s = settings[c] || {};
    const level = (s.categoryLevels || {})[catKey] || "1";
    const gt = lookupSlab((settings[`${c}GT`] || {})[level] || [], sp);
    const net = sp - gt;
    const ff = lookupSlab(settings[`${c}FixedFee`] || [], net);
    let commPct = 0;
    for (const pr of (s.categories || {})[catKey]?.priceRanges || []) {
      const mn = +(pr.min || 0), mx = pr.max === "" || pr.max == null ? Infinity : +pr.max;
      if (sp >= mn && sp <= mx) { commPct = +(pr.commission || 0); break; }
    }
    const commAmt = net * commPct / 100;
    const prodRetPct = lookupSlab(getReturnSlabs(settings, c, catKey), sp);
    const chSR = _chSalesReturn?.[c];
    const actualRetPct = chSR && chSR.salesQty > 0 ? (chSR.returnQty / chSR.salesQty) * 100 : styleReturnPct;
    const effRetPct = effRet(actualRetPct, prodRetPct);
    const catSplit = getZoneSplit(settings, c, catKey);
    const charges = (settings[`${c}RL`]?.charges || {})[level] || {};
    const lW = +(catSplit.local || 0) / 100, zW = +(catSplit.zonal || 0) / 100, nW = +(catSplit.national || 0) / 100;
    const weightedCharge = lW * +(charges.local || 0) + zW * +(charges.zonal || 0) + nW * +(charges.national || 0);
    const salesQty = _chSalesReturn ? (chSR?.salesQty || 0) : (raw._salesQty || 0);
    const returnQty = Math.round(salesQty * effRetPct / 100);
    const rlCostPerUnit = salesQty > 0 ? (returnQty * weightedCharge) / salesQty : 0;
    const expense = gt + commAmt + ff + rlCostPerUnit;
    ch[c] = { expense, profit: spNoTax - cp - expense, gt, comm: commAmt, ff, level, rlCostPerUnit, weightedCharge, returnQty, salesQty, productReturnPct: prodRetPct, styleReturnPct: actualRetPct, effReturnPct: effRetPct };
  }

  return { _sp: sp, _gst: gst, _spNoTax: spNoTax, _ch: ch, _effectiveDisc: effectiveDisc };
}

// suggestDiscount — UNCHANGED from profitmeasurer/src/App.js (lines ~292-306). Scans
// discount % downward from the style's current discount in 0.5% steps until the given
// channel's SPF-adjusted margin hits targetPct, or returns null if already at/above
// target (or unreachable at 0% disc).
export function suggestDiscount(raw, settings, targetPct, ch, adjSpfNetPerUnit = 0) {
  const currentDisc = raw._disc || 0;
  const current = calcRow(raw, settings);
  const curCd = current._ch[ch] || {};
  if (curCd.profit != null && current._spNoTax > 0 && (curCd.profit + adjSpfNetPerUnit) / current._spNoTax * 100 >= targetPct) return null;
  for (let d = currentDisc; d >= 0; d -= 0.5) {
    const trial = calcRow({ ...raw, _disc: d }, settings);
    const cd = trial._ch[ch] || {};
    if (cd.profit == null) continue;
    const adjProfit = cd.profit + adjSpfNetPerUnit;
    const spNoTax = trial._spNoTax;
    if (spNoTax > 0 && adjProfit / spNoTax * 100 >= targetPct) return Math.round(d * 100) / 100;
  }
  return null;
}
